import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ParceiroInsert } from "../../../../types/Parceiros";
import {
  createInstitution,
  updateInstitution,
  findInstitution,
} from "../../../../API/apiParceiros";
import { findAllTiposInstituicao } from "../../../../API/apiDadosComplementares";
import { isValidCNPJ, isValidCPF } from "../../../../utils/DocumentValidators";
import { OptionItem } from "components/atoms/SearchSelect/SearchSelect";

export const useParceiroSave = (partnerId?: string | null) => {
  const navigate = useNavigate();

  const [msgError, setMsgError] = useState<string | null>(null);
  const [msgSuccess, setMsgSuccess] = useState<string | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState<boolean>(false);
  const [loadingSave, setLoadingSave] = useState<boolean>(false);
  const [tiposInstituicao, setTiposInstituicao] = useState<OptionItem[]>([]);
  const [parceiro, setParceiro] = useState<ParceiroInsert>({
    nome: "",
    cnpj: "",
    email: "",
    telefone: "",
    resp_nome: "",
    resp_cpf: "",
    resp_email: "",
    resp_celular: "",
    tipo_id: null,
  });

  useEffect(() => {
    buscarTiposInstituicao();

    if (partnerId) {
      consultarParceiro(partnerId);
    }
  }, [partnerId]);

  const buscarTiposInstituicao = async () => {
    try {
      const result = await findAllTiposInstituicao();
      setTiposInstituicao((result || []).map((item) => ({
        id: item.id,
        nome: item.nome,
      })));
    } catch (error) {
      setTiposInstituicao([]);
    }
  };

  const consultarParceiro = async (partnerIdValue: string) => {
    setLoading(true);

    try {
      const result = await findInstitution(partnerIdValue);
      setParceiro(result);
      setErrors({});
      setLoading(false);
    } catch (error) {
      setLoading(false);
      setMsgError("Erro ao listar os parceiros");
    }
  };

  const clearFieldError = (field: string) => {
    setErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const handleRespName = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    const sanitizedValue = value.replace(/[^a-zA-ZÀ-ÿ\s]/g, "");
    setParceiro({ ...parceiro, resp_nome: sanitizedValue });
    clearFieldError("resp_nome");
  };

  const handleInputChange = (value: string | number, nameObject: string, isNumber?: boolean) => {
    setParceiro({ ...parceiro, [nameObject]: isNumber ? Number(value) : value });
    clearFieldError(nameObject);
  };

  const handleChange = (field: keyof ParceiroInsert, value: any) => {
    setParceiro({ ...parceiro, [field]: value });
    clearFieldError(String(field));
  };

  const handleEmailChange = (email: string, nameObject: string) => {
    setParceiro({ ...parceiro, [nameObject]: email });
    clearFieldError(nameObject);
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!parceiro.nome?.trim()) {
      newErrors.nome = "Nome é obrigatório";
    }

    if (!parceiro.cnpj?.trim()) {
      newErrors.cnpj = "CNPJ é obrigatório";
    } else if (!isValidCNPJ(parceiro.cnpj)) {
      newErrors.cnpj = "CNPJ inválido";
    }

    if (!parceiro.email?.trim()) {
      newErrors.email = "E-mail é obrigatório";
    }

    if (!parceiro.telefone?.trim()) {
      newErrors.telefone = "Telefone é obrigatório";
    }

    if (!parceiro.resp_nome?.trim()) {
      newErrors.resp_nome = "Nome do responsável é obrigatório";
    }

    if (!parceiro.resp_cpf?.trim()) {
      newErrors.resp_cpf = "CPF do responsável é obrigatório";
    } else if (!isValidCPF(parceiro.resp_cpf)) {
      newErrors.resp_cpf = "CPF do responsável inválido";
    }

    if (!parceiro.resp_email?.trim()) {
      newErrors.resp_email = "E-mail do responsável é obrigatório";
    }

    if (!parceiro.resp_celular?.trim()) {
      newErrors.resp_celular = "Celular do responsável é obrigatório";
    }

    if (!parceiro.tipo_id) {
      newErrors.tipo_id = "Tipo de instituição é obrigatório";
    }

    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) {
      setMsgError("Preencha os campos obrigatórios");
      return false;
    }

    setMsgError(null);
    return true;
  };

  const saveParceiro = async () => {
    if (!validateForm()) {
      return;
    }

    setLoadingSave(true);

    try {
      if (partnerId) {
        await updateInstitution(partnerId, parceiro);
        setMsgSuccess("Instituicao atualizada com sucesso!");
      } else {
        await createInstitution(parceiro);
        setMsgSuccess("Instituicao cadastrada com sucesso!");
      }

      setLoadingSave(false);
      navigate(`/partners`);
    } catch (error) {
      setLoadingSave(false);
      setMsgError("Nao foi possivel cadastrar a instituicao");
      return false;
    }
  };

  return {
    msgError,
    msgSuccess,
    errors,
    loading,
    loadingSave,
    tiposInstituicao,
    parceiro,
    setMsgError,
    setMsgSuccess,
    setParceiro,
    handleRespName,
    handleInputChange,
    handleChange,
    handleEmailChange,
    saveParceiro,
  };
};
